/** The security console: deployment posture, privacy policy, SSO and the audit log. */
const en = {
  title: 'Security',
  subtitle: 'Deployment posture and the append-only audit trail for this organization.',
  deployment: {
    heading: 'Deployment',
    mode: 'Mode',
    singleTenant: 'Single-tenant',
    multiTenant: 'Multi-tenant',
    organization: 'Organization',
    environment: 'Environment',
    version: 'Version',
    storage: 'Session storage',
    localDisk: 'Local disk',
    path: 'Path',
    bucket: 'Bucket',
    queue: 'Ingest queue',
    search: 'Search',
    searchOff: 'Disabled (Postgres)',
  },
  people: {
    heading: 'People',
    members: 'Members',
    owners: 'Owners',
    admins: 'Admins',
    activeKeys: 'Active API keys',
    roles: 'Roles',
  },
  roleNames: {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    MEMBER: 'Member',
    VIEWER: 'Viewer',
  } as Record<string, string>,
  privacy: {
    heading: 'Privacy',
    maskInputs: 'Automatic input masking',
    maskInputsBody:
      'When on, every text input and textarea value is masked in recordings across all projects. The default. Turn it off to capture typed values in the clear for debugging. Passwords, card numbers, CVVs and national-id fields are always masked regardless of this setting. New sessions pick up the change within a few seconds.',
    maskInputsWarning: 'Input values are being recorded unmasked',
    pii: 'PII masking for AI & integrations',
    scale: 'Scale',
    detectorOnline: 'detector online',
    detectorOffline: 'detector offline',
    piiBody:
      'When on, content leaving the platform (AI summary prompts and issues created in Jira/Linear and other trackers) is scrubbed first: URLs lose their query strings, tokens are redacted, and the selected identifier types below are detected (Presidio) and replaced. Recordings themselves are not modified.',
    piiUpgrade: 'Available on the Scale plan',
    piiGate: 'PII masking requires the Scale plan or higher.',
    failClosed: "Fail closed. If the detector is unreachable, don't send the content at all",
    savePolicy: 'Save PII policy',
    ingest: 'Mask PII before storage',
    enterprise: 'Enterprise',
    ingestBody:
      'When on, recorded content is scrubbed of personal data before it is stored. Page text, console output, network payloads and URLs pass through the PII detector (emails, phones, TC Kimlik No, cards, IBANs, person names) and only the masked version is kept. Permanent and irreversible for new recordings; existing recordings are unchanged. Adds processing time before a session becomes watchable.',
    ingestUpgrade: 'Available on the Enterprise plan',
    ingestGate: 'Ingest-time PII masking requires the Enterprise plan.',
  },
  entities: {
    EMAIL_ADDRESS: 'Email',
    PHONE_NUMBER: 'Phone',
    TR_PHONE: 'Phone (TR)',
    TR_TCKN: 'TC Kimlik No',
    CREDIT_CARD: 'Credit card',
    IBAN_CODE: 'IBAN',
    IP_ADDRESS: 'IP address',
    PERSON: 'Person name',
    LOCATION: 'Location',
  } as Record<string, string>,
  widgets: {
    heading: 'Visitor widgets',
    chat: 'In-page chat',
    chatBody:
      'The chat bubble the SDK adds to your site. Turn it off to record and watch sessions without offering visitors a conversation. Applies to new page loads within a few seconds. No change to your install code.',
    tester: 'Bug-report launcher',
    testerBody:
      'The 🐞 button that captures the screen, lets the person draw on it and files a finding. Independent of the chat: a team testing a site usually wants this without a support chat on their pages. It is shown to every visitor, and that is safe — filing is verified and gated server-side, so a passer-by who taps it cannot open anything.',
    chatDisabledHint: 'The live channel is off, so no widget is shown.',
    anonymous: 'Findings from people without an account',
    anonymousBody:
      'Lets anyone who taps the launcher file a finding by typing their name, without signing in to ReplayOps. For handing a site to outside testers who should not need accounts. The name is attached to the ticket; everything else — AI suggestions, filing into your tracker — stays signed-in only. Off by default: the launcher is shown to every visitor, so this decides whether a stranger can open a ticket.',
    anonymousNeedsTester: 'The bug-report launcher is off, so there is nothing to file from.',
    live: 'Live presence & co-browsing',
    liveBody:
      'Whether visitors appear on the Live screen and can be watched in real time. Turning this off also removes the widgets above. Session recording is unaffected either way.',
  },
  audit: {
    heading: 'Audit Log',
    presets: {
      'auth.login': 'Sign-ins',
      'auth.login.failed': 'Failed sign-ins',
      'session.replay.view': 'Replay views',
    } as Record<string, string>,
    filterPlaceholder: 'Filter by action… (e.g. ticket, apiKey)',
    columns: { when: 'When', action: 'Action', target: 'Target', actor: 'Actor', ip: 'IP' },
    system: 'system',
    emptyFiltered: 'No audit entries match this filter.',
    empty: 'No audit entries yet.',
  },
};

const tr: typeof en = {
  title: 'Güvenlik',
  subtitle: 'Bu organizasyon için kurulum durumu ve yalnızca eklenebilir denetim kaydı.',
  deployment: {
    heading: 'Kurulum',
    mode: 'Kip',
    singleTenant: 'Tek kiracılı',
    multiTenant: 'Çok kiracılı',
    organization: 'Organizasyon',
    environment: 'Ortam',
    version: 'Sürüm',
    storage: 'Oturum depolama',
    localDisk: 'Yerel disk',
    path: 'Yol',
    bucket: 'Kova',
    queue: 'Veri alım kuyruğu',
    search: 'Arama',
    searchOff: 'Kapalı (Postgres)',
  },
  people: {
    heading: 'Kişiler',
    members: 'Üye',
    owners: 'Sahip',
    admins: 'Yönetici',
    activeKeys: 'Etkin API anahtarı',
    roles: 'Roller',
  },
  roleNames: {
    OWNER: 'Sahip',
    ADMIN: 'Yönetici',
    MEMBER: 'Üye',
    VIEWER: 'İzleyici',
  },
  privacy: {
    heading: 'Gizlilik',
    maskInputs: 'Otomatik girdi maskeleme',
    maskInputsBody:
      'Açıkken, tüm projelerde her metin girdisinin ve metin alanının değeri kayıtlarda maskelenir. Varsayılan budur. Hata ayıklama için yazılan değerleri açık kaydetmek isterseniz kapatın. Parolalar, kart numaraları, CVV’ler ve kimlik numarası alanları bu ayardan bağımsız olarak her zaman maskelenir. Yeni oturumlar değişikliği birkaç saniye içinde alır.',
    maskInputsWarning: 'Girdi değerleri maskelenmeden kaydediliyor',
    pii: 'AI ve entegrasyonlar için PII maskeleme',
    scale: 'Scale',
    detectorOnline: 'algılayıcı çevrimiçi',
    detectorOffline: 'algılayıcı çevrimdışı',
    piiBody:
      'Açıkken platformdan çıkan içerik (AI özet istemleri ve Jira/Linear gibi sistemlerde açılan kayıtlar) önce temizlenir: URL’ler sorgu dizelerini kaybeder, belirteçler gizlenir ve aşağıda seçilen kimlik türleri algılanıp (Presidio) değiştirilir. Kayıtların kendisi değiştirilmez.',
    piiUpgrade: 'Scale planında kullanılabilir',
    piiGate: 'PII maskeleme Scale ya da üstü bir plan gerektirir.',
    failClosed: 'Kapalı başarısız ol. Algılayıcıya erişilemiyorsa içeriği hiç gönderme',
    savePolicy: 'PII politikasını kaydet',
    ingest: 'Depolamadan önce PII maskele',
    enterprise: 'Enterprise',
    ingestBody:
      'Açıkken, kaydedilen içerik depolanmadan önce kişisel verilerden arındırılır. Sayfa metni, konsol çıktısı, ağ yükleri ve URL’ler PII algılayıcıdan geçer (e-posta, telefon, TC Kimlik No, kart, IBAN, kişi adı) ve yalnızca maskelenmiş sürüm saklanır. Yeni kayıtlar için kalıcı ve geri alınamaz; mevcut kayıtlar değişmez. Bir oturumun izlenebilir hale gelmesine işlem süresi ekler.',
    ingestUpgrade: 'Enterprise planında kullanılabilir',
    ingestGate: 'Veri alımında PII maskeleme Enterprise planı gerektirir.',
  },
  entities: {
    EMAIL_ADDRESS: 'E-posta',
    PHONE_NUMBER: 'Telefon',
    TR_PHONE: 'Telefon (TR)',
    TR_TCKN: 'TC Kimlik No',
    CREDIT_CARD: 'Kredi kartı',
    IBAN_CODE: 'IBAN',
    IP_ADDRESS: 'IP adresi',
    PERSON: 'Kişi adı',
    LOCATION: 'Konum',
  },
  widgets: {
    heading: 'Ziyaretçi bileşenleri',
    chat: 'Sayfa içi sohbet',
    chatBody:
      'SDK’nın sitenize eklediği sohbet balonu. Ziyaretçilere sohbet sunmadan oturum kaydetmek ve izlemek için kapatın. Yeni sayfa yüklemelerinde birkaç saniye içinde geçerli olur. Kurulum kodunuzda değişiklik gerekmez.',
    tester: 'Bulgu bildirme düğmesi',
    testerBody:
      'Ekranı yakalayan, üzerine çizim yaptırıp bulgu açan 🐞 düğmesi. Sohbetten bağımsızdır: bir siteyi test eden ekip çoğunlukla bunu sayfalarında destek sohbeti olmadan ister. Her ziyaretçiye görünür ve bu güvenlidir — bulgu açma sunucuda kimlik doğrulamasından geçer, tıklayan bir yoldan geçen hiçbir şey açamaz.',
    chatDisabledHint: 'Canlı kanal kapalı, bu yüzden hiçbir bileşen gösterilmiyor.',
    anonymous: 'Hesabı olmayanlardan bulgu',
    anonymousBody:
      'Düğmeye dokunan herkesin, ReplayOps’a giriş yapmadan adını yazarak bulgu açmasını sağlar. Siteyi hesap açmasına gerek olmayan dış test ekiplerine verdiğinizde işe yarar. Ad bilete iliştirilir; AI önerisi ve tracker’a kayıt açma gibi her şey yalnızca girişli kullanıcıda kalır. Varsayılan kapalı: düğme her ziyaretçiye görünür, yani bu anahtar bir yabancının bilet açıp açamayacağına karar verir.',
    anonymousNeedsTester: 'Bulgu bildirme düğmesi kapalı, bildirilecek bir yer yok.',
    live: 'Canlı varlık ve ortak gezinme',
    liveBody:
      'Ziyaretçilerin Canlı ekranında görünüp gerçek zamanlı izlenip izlenemeyeceği. Bunu kapatmak yukarıdaki bileşenleri de kaldırır. Oturum kaydı her iki durumda da etkilenmez.',
  },
  audit: {
    heading: 'Denetim Kaydı',
    presets: {
      'auth.login': 'Girişler',
      'auth.login.failed': 'Başarısız girişler',
      'session.replay.view': 'Kayıt görüntülemeleri',
    },
    filterPlaceholder: 'Eyleme göre filtrele… (örn. ticket, apiKey)',
    columns: { when: 'Ne zaman', action: 'Eylem', target: 'Hedef', actor: 'Aktör', ip: 'IP' },
    system: 'sistem',
    emptyFiltered: 'Bu filtreye uyan denetim kaydı yok.',
    empty: 'Henüz denetim kaydı yok.',
  },
};

export const security = { en, tr };
